import { useEffect, useRef, useState } from "react";
import { api } from "../api/client.js";

/**
 * FIX-011 / RE Stage 7 - the ADVISORY quality check of a generated RE video, shown next to the video in Review (Gate 2 and Final Review) and on the RE screen. It is information only: it never
 * blocks, delays or changes Approve / Reject. For a video that is not an RE video (every RP video) the server answers `{ re: false }` once and this renders nothing (and stops asking).
 * A failed request renders nothing too - the Review screen must work exactly as before whatever happens here.
 */
export type ReQcResult = "MATCH" | "MISMATCH" | "UNRESOLVED";
export interface ReVideoQcDto {
  readonly re: boolean;
  readonly qc: null | {
    readonly status: "requested" | "running" | "completed" | "failed";
    readonly attempts: number;
    readonly checkResults: Record<string, ReQcResult> | null;
    readonly deterministicChecks: Record<string, Record<string, unknown>> | null;
    readonly modelFindings: { readonly answeredPasses?: number; readonly passes?: number; readonly checks?: Record<string, { readonly passes?: (ReQcResult | null)[]; readonly evidence?: string[] }> } | null;
    readonly errorCode: string | null;
    readonly completedAt: string | null;
  };
}

export const RE_QC_LABELS: Readonly<Record<string, string>> = {
  persona_identity: "Persona identity", other_characters: "Other characters", scene_and_order: "Scenes and order of events", actions: "Actions", body_expressions_gestures: "Body, expressions and gestures",
  dialogue: "Dialogue", speakers: "Speakers", outfit_accessories: "Outfit and accessories", camera_framing: "Camera and framing", duration: "Duration", technical_integrity: "Technical integrity", music_effects: "Music and effects",
};
const ORDER = ["persona_identity", "other_characters", "scene_and_order", "actions", "body_expressions_gestures", "dialogue", "speakers", "outfit_accessories", "camera_framing", "duration", "technical_integrity", "music_effects"];
const COLOR: Record<ReQcResult, string> = { MATCH: "var(--success, #15803d)", MISMATCH: "var(--danger, #b91c1c)", UNRESOLVED: "var(--muted, #6b7280)" };
const POLL_MS = 15_000;

function detail(name: string, view: NonNullable<ReVideoQcDto["qc"]>): string {
  const d = view.deterministicChecks?.[name];
  if (d !== undefined) {
    if (name === "duration") return `requested ${String(d["requestedSeconds"])} s, measured ${String(d["actualSeconds"])} s`;
    if (name === "technical_integrity") return Array.isArray(d["problems"]) && d["problems"].length > 0 ? (d["problems"] as string[]).join("; ") : `${String(d["width"])}x${String(d["height"])}, audio ${d["hasAudio"] === true ? "present" : "missing"}, checksum verified`;
    if (name === "dialogue") return d["error"] !== undefined ? `could not be measured (${String(d["error"])})` : `word error ${d["wer"] === null || d["wer"] === undefined ? "n/a" : String(d["wer"])} · expected “${String(d["expectedText"] ?? "")}” · heard “${String(d["heardText"] ?? "")}”`;
  }
  const m = view.modelFindings?.checks?.[name];
  if (m !== undefined) return `pass 1: ${m.passes?.[0] ?? "no answer"} · pass 2: ${m.passes?.[1] ?? "no answer"}${(m.evidence ?? []).filter((e) => e !== "").length > 0 ? ` — ${(m.evidence ?? []).filter((e) => e !== "").join(" / ")}` : ""}`;
  return "";
}

export function ReQcPanel({ tenantId, videoId }: { readonly tenantId: string; readonly videoId: string }) {
  const [view, setView] = useState<ReVideoQcDto | null>(null);
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);

  useEffect(() => {
    let timer: number | undefined;
    let stopped = false;
    const load = async (): Promise<void> => {
      try {
        const v = await api.get<ReVideoQcDto>(`/re/${tenantId}/videos/${encodeURIComponent(videoId)}/qc`);
        if (stopped || !alive.current || v === undefined || v === null || typeof v !== "object") return;
        setView(v);
        if (v.re && (v.qc === null || v.qc.status === "requested" || v.qc.status === "running")) timer = window.setTimeout(() => void load(), POLL_MS);
      } catch {
        // Nothing to show - the Review screen is unaffected.
      }
    };
    void load();
    return () => { stopped = true; if (timer !== undefined) window.clearTimeout(timer); };
  }, [tenantId, videoId]);

  if (view === null || !view.re) return null;
  const qc = view.qc;
  const results = qc?.checkResults ?? null;
  const counts = results === null ? null : (["MATCH", "MISMATCH", "UNRESOLVED"] as const).map((k) => [k, Object.values(results).filter((r) => r === k).length] as const);
  return (
    <section data-testid="re-qc" style={{ marginTop: 12, padding: 10, border: "1px solid var(--border, #444)", borderRadius: 6 }}>
      <strong>Quality check against the approved proposal</strong>{" "}
      <span className="muted" data-testid="re-qc-advisory">Advisory only — it does not affect Approve or Reject.</span>
      {qc === null || qc.status === "requested" || qc.status === "running" ? <p className="muted" data-testid="re-qc-pending" style={{ marginBottom: 0 }}>The quality check has not finished yet; this updates by itself.</p> : null}
      {qc !== null && qc.status === "failed" ? <p className="muted" data-testid="re-qc-failed" style={{ marginBottom: 0 }}>The quality check could not be completed ({qc.errorCode ?? "error"}{qc.attempts > 1 ? `, ${qc.attempts} attempts` : ""}). The video is not affected.</p> : null}
      {qc !== null && qc.status === "completed" && results !== null ? (
        <>
          <p data-testid="re-qc-summary" style={{ margin: "6px 0" }}>{counts!.map(([k, n]) => `${k} ${n}`).join(" · ")}</p>
          <table data-testid="re-qc-table" style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
            <tbody>
              {ORDER.filter((n) => results[n] !== undefined).map((name) => {
                const measured = qc.deterministicChecks?.[name] !== undefined;
                return (
                  <tr key={name} data-testid="re-qc-row" data-check={name} data-result={results[name]} style={{ verticalAlign: "top" }}>
                    <td style={{ padding: "2px 8px 2px 0", whiteSpace: "nowrap" }}>{RE_QC_LABELS[name] ?? name}</td>
                    <td style={{ padding: "2px 8px 2px 0", color: COLOR[results[name]!], fontWeight: 600 }}>{results[name]}</td>
                    <td className="muted" style={{ padding: "2px 8px 2px 0", whiteSpace: "nowrap" }}>{measured ? "measured" : "model assessment"}</td>
                    <td className="muted" style={{ padding: "2px 0" }}>{detail(name, qc)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p className="muted" style={{ marginBottom: 0, fontSize: 12 }}>“Measured” results come from direct measurement of the file and its audio. “Model assessment” results are an AI model's opinion from two independent viewings; they count only when both agree, and are not proof.</p>
        </>
      ) : null}
    </section>
  );
}
